import { onUnmounted } from "vue";
import { useStorage } from "vue3-storage";
import { getDeviceUnitUid } from "@/utils/deviceUnitUid";
import {
  collectDeviceMetrics,
  deriveClientKind,
  sendHeartbeat,
} from "@/utils/api/deviceHeartbeat";
import type { HeartbeatPayload } from "@/utils/api/deviceHeartbeat";
import { isHeartbeatEnabled } from "@/composables/deviceSettingsGate";
// プレビュー閲覧 (人がレイアウトを見に来ている) は端末として記録しない。
// 判定は utils/previewMode が正本 (URL 規約を 1 箇所に持つ)。
import { isPreviewMode } from "@/utils/previewMode";
import {
  BatteryAlertMonitor,
  PowerTransitionDetector,
  enqueuePowerEvent,
  markKioskBatteryReported,
  parsePendingPowerEvents,
  readKioskBattery,
  readKioskInfo,
  toBatteryPayload,
  toPendingPowerEvent,
} from "@/utils/kioskBattery";
import type { PendingPowerEvent } from "@/utils/kioskBattery";

const INTERVAL_MS = 24 * 60 * 60 * 1000; // 24h（死活には十分。 端末増でも実質無コスト）
const JITTER_MAX_MS = 5 * 60 * 1000; // 起動時 beat の最大ジッター（5分）。 一斉再起動の集中回避
// ↑ 500端末でも同時再起動時 ≈1.7 req/s と軽く、 死活/backfill も最大5分で走る妥当値。
const BACKFILL_DONE_KEY = "deviceSettingsBackfilledAt";
// バッテリー閾値監視の間隔。KioskBridge をローカルに読むだけ (通信なし)。
// 臨時 heartbeat は閾値超過の瞬間のみ = 平常時の通信量は定期便 (24h) のまま。
const BATTERY_WATCH_MS = 5 * 60 * 1000;
// 未送信の給電の入・切 (回線もタイマーで切れる施設では翌朝まで送れない)。電池切れでページが
// 落ちても残るよう localStorage に置く。
const PENDING_POWER_EVENTS_KEY = "pendingPowerEvents";

interface StorageLike {
  getStorageSync: <T = unknown>(key: string) => T;
  setStorageSync: (key: string, value: unknown) => void;
}

// 自己リロードの諦め状態 (public/release-watch.js が書く。§6.2)
const RELEASE_STALE_KEY = "adbox_release.staleSince";

const readReleaseStale = (): boolean => {
  try {
    return !!window.localStorage.getItem(RELEASE_STALE_KEY);
  } catch {
    return false;
  }
};

const toBoolean = (v: unknown): boolean => v === true || v === "true";

// localStorage の端末設定 (deviceLocalSettings) を backfill payload 用に集める。 無ければ undefined。
const collectLocalSettings = (
  storage: StorageLike
): Record<string, unknown> | undefined => {
  const settings: Record<string, unknown> = {};
  const layout = storage.getStorageSync<string>("displayLayout");
  const theme = storage.getStorageSync<string>("verticalTheme");
  if (layout) settings.displayLayout = layout;
  if (theme) settings.verticalTheme = theme;
  settings.isAudio = toBoolean(storage.getStorageSync("isAudio"));
  settings.isLogVisible = toBoolean(storage.getStorageSync("isLogVisible"));
  return Object.keys(settings).length ? settings : undefined;
};

/**
 * 端末 heartbeat を起動時 ＋ 24h 毎に送る。 ゲート (isHeartbeatEnabled) が無効な端末/環境
 * では何もしない。 初回は端末設定を同梱して backfill する（server 側 first-write-wins）。
 *
 * 設計: adbox-admin-rails docs/design/features/device_telemetry.md
 */
export function useHeartbeat(): void {
  const storage = useStorage() as unknown as StorageLike;
  let timer: number | null = null;
  let batteryTimer: number | null = null;
  const alertMonitor = new BatteryAlertMonitor();
  const powerDetector = new PowerTransitionDetector();
  let flushingPowerEvents = false;

  // この端末から報告してよいか (枠未設定・ゲート無効・人のプレビュー閲覧は記録しない §12.6)。
  const canReport = (): string | null => {
    const terminalId = storage.getStorageSync<string>("terminalId");
    if (!terminalId || !isHeartbeatEnabled(terminalId) || isPreviewMode()) {
      return null;
    }
    return terminalId;
  };

  /**
   * heartbeat を 1 回送る。送れたら true。
   *   - batteryAlert: 閾値警報による臨時送信
   *   - powerEvent: 給電の入・切の報告。測定値は観測した瞬間のものを送る
   */
  const beat = async ({
    batteryAlert = false,
    powerEvent,
  }: {
    batteryAlert?: boolean;
    powerEvent?: PendingPowerEvent;
  } = {}): Promise<boolean> => {
    try {
      const terminalId = canReport();
      if (!terminalId || !navigator.onLine) return false;

      const deviceUnitUid = await getDeviceUnitUid(storage);
      const payload: HeartbeatPayload = collectDeviceMetrics(deviceUnitUid);

      // 初回のみ端末設定を同梱して backfill (server 側 first-write-wins なので再送は no-op)。
      if (!storage.getStorageSync(BACKFILL_DONE_KEY)) {
        const ls = collectLocalSettings(storage);
        if (ls) payload.deviceLocalSettings = ls;
      }

      // 電池内蔵端末 (KioskBridge あり) のみバッテリー測定を同梱。無ければ従来と同一挙動。
      // 給電の入・切の報告は観測時点の測定値を送る (いまの値を付けると時点がずれる)。
      const battery = powerEvent ? undefined : readKioskBattery();
      if (powerEvent) payload.battery = powerEvent.battery;
      else if (battery)
        payload.battery = toBatteryPayload(battery, batteryAlert);

      // キオスク状態 (適用configVersion/APK版)。管理画面で「configが届いているか」を見るための報告。
      const kiosk = readKioskInfo();
      if (kiosk) payload.kiosk = kiosk;

      // 配信 Web の版と端末種別の自己申告 (release_rollout.md §9 / §12.6)。
      // 管理画面で「リリースの取り残し」を検知し、実機とヒト閲覧を切り分けるための材料。
      const buildId = process.env.VUE_APP_BUILD_ID;
      if (buildId) payload.appBuildId = buildId;
      if (readReleaseStale()) payload.appBuildStale = true;
      // KioskBridge の存在自体で判定 (古い APK で getKioskInfo が無くても bridge はある)
      payload.clientKind = deriveClientKind(
        "KioskBridge" in window,
        window.location.hostname
      );

      const ok = await sendHeartbeat(terminalId, payload);
      if (ok) {
        storage.setStorageSync(BACKFILL_DONE_KEY, new Date().toISOString());
        // 送信成功時のみ DPC 側のピーク温度をリセット (失敗時はピークを持ち越す契約)。
        // 給電の入・切の報告は観測時点の値なので、いまのピークはリセットしない。
        if (battery) markKioskBatteryReported();
      }
      return ok;
    } catch (e) {
      // heartbeat はベストエフォート。 想定外 (古い webview の API 欠落等) でも端末の
      // 表示動作には一切影響させない。
      console.warn("[heartbeat] beat 失敗:", e);
      return false;
    }
  };

  const readPendingPowerEvents = (): PendingPowerEvent[] =>
    parsePendingPowerEvents(storage.getStorageSync(PENDING_POWER_EVENTS_KEY));

  // 溜まっている給電の入・切を古い順に送る。送れなかったらそこで止め、次の監視周期で続きを送る。
  const flushPowerEvents = async (): Promise<void> => {
    if (flushingPowerEvents) return;
    flushingPowerEvents = true;
    try {
      let queue = readPendingPowerEvents();
      while (queue.length > 0) {
        if (!(await beat({ powerEvent: queue[0] }))) break;
        queue = queue.slice(1);
        storage.setStorageSync(PENDING_POWER_EVENTS_KEY, queue);
      }
    } finally {
      flushingPowerEvents = false;
    }
  };

  // 閾値監視 (温度 / 非給電中の残量急落)。超過の瞬間だけ臨時 heartbeat を送る。
  // 閾値・ヒステリシスは kioskBattery.ts 側 (純ロジック・テスト対象)。
  // あわせて給電の入・切を検知し、確定したら観測時点の測定値つきで溜めてから送る。
  const watchBattery = (): void => {
    try {
      const info = readKioskBattery();
      if (!info) return;
      const now = Date.now();
      if (alertMonitor.evaluate(info, now).alert)
        void beat({ batteryAlert: true });

      const power = powerDetector.observe(info.charging, now);
      if (power && canReport()) {
        storage.setStorageSync(
          PENDING_POWER_EVENTS_KEY,
          enqueuePowerEvent(
            readPendingPowerEvents(),
            toPendingPowerEvent(power, info)
          )
        );
      }
      void flushPowerEvents();
    } catch (e) {
      console.warn("[heartbeat] battery 監視失敗:", e);
    }
  };

  // 起動時 beat を 0〜JITTER_MAX_MS のランダム遅延で散らす (一斉再起動でのリクエスト集中=
  // thundering herd を回避)。 24h 間隔の位相も初回遅延でずれ、 端末ごとに送信時刻が分散する。
  // ローカル開発は検証のため即時 beat (ジッター無し)。
  const jitterMs =
    process.env.NODE_ENV !== "production"
      ? 0
      : Math.floor(Math.random() * JITTER_MAX_MS);
  const startTimer = setTimeout(() => {
    void beat();
    timer = setInterval(() => void beat(), INTERVAL_MS) as unknown as number;
  }, jitterMs) as unknown as number;

  // バッテリー監視は通信を伴わないためジッター不要で即開始 (ブリッジ無し端末では毎回 no-op)。
  batteryTimer = setInterval(
    watchBattery,
    BATTERY_WATCH_MS
  ) as unknown as number;

  onUnmounted(() => {
    clearTimeout(startTimer);
    if (timer) clearInterval(timer);
    if (batteryTimer) clearInterval(batteryTimer);
  });
}
